import { authDebugLog, getFreshUserAccessToken, safeRefresh } from "@/lib/accessTokenRefresh";

/**
 * Normalize `VITE_API_URL` for Vite + Cloudflare Pages.
 * - `undefined` / `null` → `""`
 * - Whitespace-only (e.g. Pages using `" "` when empty is disallowed) → `""`
 * - Otherwise: trim, then strip trailing slashes (safe joins with paths that start with `/`).
 */
export function normalizeViteApiUrl(raw: string | undefined | null): string {
  if (raw === undefined || raw === null) return "";
  const t = String(raw).trim();
  if (!t) return "";
  return t.replace(/\/+$/, "");
}

/**
 * Canonical API origin for the FastSell BFF (`/api/*` on the main site).
 * - Prefer explicit `VITE_API_URL` (e.g. `https://usefastsell.com`).
 * - **Production:** if unset or empty after trim, fall back to `VITE_PUBLIC_APP_ORIGIN` so storefronts
 *   on merchant custom domains still call `https://usefastsell.com/api/...` (never `https://merchant.com/api/...`).
 * - **Development:** empty explicit `VITE_API_URL` keeps `""` for Vite `/api` → `DEV_API_PROXY` (same-origin).
 */
export function getAppApiBase(): string {
  const raw = import.meta.env.VITE_API_URL as string | undefined;
  const explicit = normalizeViteApiUrl(raw);
  if (explicit) return explicit;
  if (import.meta.env.DEV) return "";
  return normalizeViteApiUrl(import.meta.env.VITE_PUBLIC_APP_ORIGIN as string | undefined);
}

/**
 * True when PostgREST + `appFetch` should use the BFF (`/api/app/...`) instead of direct Supabase REST.
 * In dev, true if `VITE_API_URL` is set (any value) or same-origin proxy is used (`VITE_API_URL` key present as empty + proxy).
 */
export function isAppApiConfigured(): boolean {
  if (getAppApiBase() !== "") return true;
  return import.meta.env.DEV && import.meta.env.VITE_API_URL !== undefined;
}

/** Attach browser storefront hostname for central-API requests (custom domain or tenant host). */
export function appendStorefrontHostHeader(headers: Headers): void {
  if (typeof window === "undefined") return;
  const host = window.location.hostname;
  if (host) headers.set("X-Storefront-Host", host);
}

/**
 * Join base + path without double slashes. `base === ""` → `path` only (must start with `/`).
 */
export function joinAppApiPath(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  const b = getAppApiBase().replace(/\/+$/, "");
  const out = b ? `${b}${p}` : p;
  if (!import.meta.env.DEV && out.startsWith("/") && p.startsWith("/api")) {
    throw new Error(
      "joinAppApiPath: relative /api URL in production. Set VITE_API_URL or VITE_PUBLIC_APP_ORIGIN.",
    );
  }
  return out;
}

/**
 * Authenticated fetch to the app API (absolute URL in production). Retries once on 401 after mutex refresh.
 */
export async function fetchWithAuth(input: string, init: RequestInit = {}): Promise<Response> {
  if (!isAppApiConfigured()) {
    throw new Error(
      "BFF not configured. Set VITE_API_URL or VITE_PUBLIC_APP_ORIGIN for production.",
    );
  }
  const url = /^https?:\/\//i.test(input) ? input : joinAppApiPath(input);
  let token = await getFreshUserAccessToken();
  if (!token) throw new Error("Not authenticated");
  const exec = async (t: string) => {
    const headers = new Headers(init.headers);
    appendStorefrontHostHeader(headers);
    headers.set("Authorization", `Bearer ${t}`);
    return fetch(url, { ...init, headers });
  };
  let res = await exec(token);
  if (res.status === 401) {
    authDebugLog("appFetch fetchWithAuth: 401 → safeRefresh + retry");
    await safeRefresh();
    token = await getFreshUserAccessToken();
    if (!token) throw new Error("Not authenticated");
    res = await exec(token);
  }
  return res;
}

type AppFetchOptions = RequestInit & {
  /** JSON body; sets Content-Type and serializes */
  json?: unknown;
  /** When true (default), attaches Bearer from the current Supabase session */
  auth?: boolean;
};

/**
 * Calls the FastSell Express `/api/app/*` surface (JWT → RLS on the server).
 * @example fetch(joinAppApiPath("/api/app/me/profile"))
 */
export async function appFetch<T = unknown>(path: string, opts: AppFetchOptions = {}): Promise<T> {
  if (!isAppApiConfigured()) {
    throw new Error(
      "BFF not configured. Set VITE_API_URL to your central API origin (e.g. https://usefastsell.com), or VITE_PUBLIC_APP_ORIGIN for production fallback. Local dev: VITE_API_URL=http://localhost:8787 or empty + DEV_API_PROXY.",
    );
  }
  const url = joinAppApiPath(path);
  const { json, auth: useAuth = true, headers: hdrs, body, ...rest } = opts;

  const buildHeaders = async (token: string | null) => {
    const headers = new Headers(hdrs);
    appendStorefrontHostHeader(headers);
    if (json !== undefined && body === undefined) {
      headers.set("Content-Type", "application/json");
    }
    if (useAuth) {
      if (!token) throw new Error("Not authenticated");
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  };

  let token = useAuth ? await getFreshUserAccessToken() : null;
  if (useAuth && !token) throw new Error("Not authenticated");

  let res = await fetch(url, {
    ...rest,
    headers: await buildHeaders(token),
    body: json !== undefined ? JSON.stringify(json) : body,
  });

  if (useAuth && res.status === 401) {
    authDebugLog("appFetch: 401 → safeRefresh + retry");
    await safeRefresh();
    token = await getFreshUserAccessToken();
    if (!token) throw new Error("Not authenticated");
    res = await fetch(url, {
      ...rest,
      headers: await buildHeaders(token),
      body: json !== undefined ? JSON.stringify(json) : body,
    });
  }

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`API returned non-JSON (HTTP ${res.status})`);
  }
  if (!res.ok) {
    const msg =
      parsed && typeof parsed === "object" && "error" in parsed && typeof (parsed as { error: unknown }).error === "string"
        ? (parsed as { error: string }).error
        : `Request failed (HTTP ${res.status})`;
    throw new Error(msg);
  }
  return parsed as T;
}

/** Public `/api/app/public/*` routes (no Authorization header). */
export async function appFetchPublic<T = unknown>(
  path: string,
  opts: Omit<AppFetchOptions, "auth"> = {},
): Promise<T> {
  return appFetch<T>(path, { ...opts, auth: false });
}
